import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import type { Video, VideoStatus } from '../types/database.types'
import type { RealtimeChannel } from '@supabase/supabase-js'

export interface VideoStatusUpdate {
  videoId: string
  oldStatus: VideoStatus | null
  newStatus: VideoStatus
  video: Video
}

interface UseVideoStatusSubscriptionOptions {
  /** User ID to filter videos (required for security) */
  userId: string
  /** Callback when a video status changes */
  onStatusChange?: (update: VideoStatusUpdate) => void
  /** Callback for any video update (broader than just status) */
  onVideoUpdate?: (video: Video) => void
  /** Callback when a new video is inserted */
  onVideoInsert?: (video: Video) => void
  /** Whether the subscription is enabled */
  enabled?: boolean
}

/**
 * Hook to subscribe to real-time video status changes via Supabase Realtime.
 *
 * Uses Postgres Changes to listen for UPDATE and INSERT events on the videos table,
 * filtered by user_id for security.
 *
 * @example
 * ```tsx
 * useVideoStatusSubscription({
 *   userId: profile.id,
 *   onStatusChange: ({ videoId, newStatus, oldStatus }) => {
 *     if (newStatus === 'ready') {
 *       toast.success('Your video is ready!')
 *     }
 *   },
 *   onVideoUpdate: (video) => {
 *     // Update local state with new video data
 *     setVideos(prev => prev.map(v => v.id === video.id ? video : v))
 *   }
 * })
 * ```
 */
export function useVideoStatusSubscription({
  userId,
  onStatusChange,
  onVideoUpdate,
  onVideoInsert,
  enabled = true,
}: UseVideoStatusSubscriptionOptions) {
  // Use refs to avoid recreating subscription on callback changes
  const onStatusChangeRef = useRef(onStatusChange)
  const onVideoUpdateRef = useRef(onVideoUpdate)
  const onVideoInsertRef = useRef(onVideoInsert)

  // Keep refs updated
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange
  }, [onStatusChange])

  useEffect(() => {
    onVideoUpdateRef.current = onVideoUpdate
  }, [onVideoUpdate])

  useEffect(() => {
    onVideoInsertRef.current = onVideoInsert
  }, [onVideoInsert])

  useEffect(() => {
    if (!enabled || !userId) return

    // Create channel with unique name to avoid conflicts
    const channelName = `video-status-${userId}-${Date.now()}`

    const channel: RealtimeChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'videos',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newVideo = payload.new as Video
          const oldVideo = payload.old as Partial<Video>

          // Always call onVideoUpdate for any change
          if (onVideoUpdateRef.current) {
            onVideoUpdateRef.current(newVideo)
          }

          // Check if status specifically changed
          if (oldVideo.status !== newVideo.status && onStatusChangeRef.current) {
            onStatusChangeRef.current({
              videoId: newVideo.id,
              oldStatus: (oldVideo.status as VideoStatus) || null,
              newStatus: newVideo.status,
              video: newVideo,
            })
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'videos',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newVideo = payload.new as Video

          if (onVideoInsertRef.current) {
            onVideoInsertRef.current(newVideo)
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[useVideoStatusSubscription] Subscribed to video updates')
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[useVideoStatusSubscription] Channel error')
        }
      })

    // Cleanup on unmount or dependency change
    return () => {
      console.log('[useVideoStatusSubscription] Unsubscribing from video updates')
      supabase.removeChannel(channel)
    }
  }, [userId, enabled])
}
